import { Component, OnInit } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { RoutePartsService } from '@egret/shared/services/route-parts.service';
import { concatMap, filter, skip, startWith, take } from 'rxjs/operators';
import { UILibIconService } from '@egret/shared/services/ui-lib-icon.service';
import { UserService } from 'bookraw/shared/services/user.service';
import { UserRole, UserType } from 'bookraw/shared/models/user.model';
import { UsersService } from 'bookraw/shared/services/users.service';
import { TranslateService } from '@ngx-translate/core';
import { environment } from 'env/environment';
import { combineLatest } from 'rxjs';
import { isFeatureDisabled } from 'bookraw/shared/directives/require-feature.directive';
import Swal from 'sweetalert2';

declare const gtag: Function;

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  providers: [
    TranslateService
  ]
})
export class AppComponent implements OnInit {
  pageTitle = '';

  private extraScriptLoaded = false;

  constructor(
    public title: Title,
    private router: Router,
    private activeRoute: ActivatedRoute,
    private routePartsService: RoutePartsService,
    private iconService: UILibIconService,
    private userService: UserService,
    private usersService: UsersService,
    private translateService: TranslateService,
    private meta: Meta
  ) {
    iconService.init()

    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe((event: NavigationEnd) => {
       if ( environment.googlClientId) gtag('event', 'page_view', {
          page_path: event.urlAfterRedirects
       })
    })
  }

  ngOnInit() {
    combineLatest([
      this.router.events.pipe(filter(event => event instanceof NavigationEnd)),
      this.translateService.onLangChange.asObservable().pipe(startWith(''))
    ]).subscribe((routeChange) => {
      this.changePageTitle();
      this.userService.updateMe(this.usersService);
    });
    document.body.classList.add(environment.theme);
    this.userService.schoolChange.subscribe((school) => {
      // Amennyiben a felhasználó belép tanárként, akkor betöltjük a szkriptet, majd úgy hagyjuk az egyyzerűség kedvéért
      // (a tanárok külön oldalon lépnek be)
      if (!this.extraScriptLoaded && (school?.role === UserRole.TEACHER || school?.role === UserRole.OPERATOR)) {
        this.addExtraScript();
        this.addSupportAI();
        this.extraScriptLoaded = true;
      }
    });
    // Minden bejelentkezéskor az iskolaválasztás után megjelenítjük a popupot
    this.userService.userLogin.pipe(concatMap(() => this.userService.schoolChange.pipe(skip(1), take(1)))).subscribe(school => {
      if (school?.role === UserRole.TEACHER || school?.role === UserRole.OPERATOR) {
        this.showTDPopup().then();
      }
    })
  }

  /**
   * Extra szkript hozzáadása TD-hez
   */
  addExtraScript() {
    if (!environment.extraScript?.length) {
      return;
    }
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.innerHTML = environment.extraScript;
    document.body.appendChild(script);
  }

  addSupportAI() {
    if (isFeatureDisabled('supportAi')) {
      return;
    }
    const script = document.createElement('script');
    script.src = environment.supportAiScriptUrl;
    script.dataset.target = '#ai-ta-root';
    document.body.appendChild(script);
  }

  async showTDPopup() {
    if (isFeatureDisabled('tdRedesign')) {
      return;
    }
    const translated = await this.translateService.get([
      'teacher.popup_title',
      'teacher.popup_content',
      'teacher.popup_confirm'
    ]).toPromise();
    await Swal.fire({
      titleText: translated['teacher.popup_title'],
      html: translated['teacher.popup_content'],
      showCloseButton: true,
      showConfirmButton: true,
      confirmButtonText: translated['teacher.popup_confirm'],
      width: '50rem'
    });
  }

  changePageTitle() {
    const routeParts = this.routePartsService.generateRouteParts(this.activeRoute.snapshot);
    let appTitle = '';
    if (!routeParts.length) {
      return this.title.setTitle(appTitle);
    }

    // Extract title from parts;
    this.pageTitle = routeParts
      .reverse()
      .map((part) => this.translateService.instant(part.title))
      .reduce((partA, partI) => {
        return `${partA} > ${partI}`
      });

    if (!this.userService.user) {
      appTitle = environment.title;
      this.meta.updateTag({ name: 'description', content: ''});
    } else if (this.userService.user.type === UserType.ADMIN) {
      appTitle = 'Admin - ' + environment.title;
      this.meta.updateTag({ name: 'description', content: ''});
    } else {
      appTitle = 'Teacher`s Dashboard - ' + environment.title;
      this.meta.updateTag({ name: 'description', content:  environment.title + ' is a digital library of animated books and games. With our English teaching app we help learn language in a playful and easy way.' });
    }

    this.pageTitle += ` | ${appTitle}`;
    this.title.setTitle(this.pageTitle);
  }
}
